/**
 * .tuMetrics {"tu1": 0.93, "tu2": 0.95, "tu3": 2, "notes": "Approval workflow diff + Review gate helpers", "tuVersion": "2.5"}
 * .help "Who was added, removed, or decided on a card approval list — and whether Review can auto-close to Done."
 */
import type { Approver, ApprovalStatus, Card, CardStatus } from '../../types/index.ts';

export function diffApprovalWorkflow(
  oldApprovers: Approver[],
  newApprovers: Approver[],
): {
  added: Approver[];
  removed: Approver[];
  statusChanges: Approver[];
} {
  const added = newApprovers.filter(
    (next) => !oldApprovers.some((prev) => prev.userId === next.userId),
  );
  const removed = oldApprovers.filter(
    (prev) => !newApprovers.some((next) => next.userId === prev.userId),
  );
  const statusChanges = newApprovers.filter((next) => {
    const prev = oldApprovers.find((old) => old.userId === next.userId);
    return prev != null && prev.status !== next.status;
  });
  return { added, removed, statusChanges };
}

/** True when this user still owes an approval on the card. */
export function isPendingApprover(
  card: Pick<Card, 'approvalWorkflow'> | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!userId || !card?.approvalWorkflow?.length) return false;
  return card.approvalWorkflow.some((a) => a.userId === userId && a.status === 'pending');
}

/** All listed approvers have approved (empty list = nothing to gate). */
export function allApproversApproved(workflow: Approver[] | null | undefined): boolean {
  if (!workflow?.length) return false;
  return workflow.every((a) => a.status === 'approved');
}

/** Apply approve/reject for one user; returns a new array (or null if no change). */
export function withApproverDecision(
  workflow: Approver[] | null | undefined,
  userId: string,
  status: Extract<ApprovalStatus, 'approved' | 'rejected'>,
  reason?: string,
): Approver[] | null {
  const list = workflow ?? [];
  const idx = list.findIndex((a) => a.userId === userId);
  if (idx < 0) return null;
  const current = list[idx];
  if (current.status === status && (reason == null || current.reason === reason)) return null;
  const next = [...list];
  next[idx] = {
    ...current,
    status,
    approvedAt: new Date().toISOString(),
    reason: reason ?? current.reason,
  };
  return next;
}

/** Pending approvers excluding the actor (who does not need a self-ping). */
export function pendingApproverIds(
  workflow: Approver[] | null | undefined,
  exceptUserId?: string | null,
): string[] {
  return (workflow ?? [])
    .filter((a) => a.status === 'pending' && a.userId !== exceptUserId)
    .map((a) => a.userId);
}

/**
 * Human label for an approver row.
 * pending before the gate → Assigned; once in Review/Done waiting → Waiting for … review.
 */
export function approverDisplayLabel(
  status: ApprovalStatus,
  cardStatus: CardStatus | null | undefined,
  opts?: { viewerIsApprover?: boolean },
): string {
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Changes requested';
  const gateOpen = cardStatus === 'under_review' || cardStatus === 'done';
  if (gateOpen) {
    return opts?.viewerIsApprover ? 'Waiting for your review' : 'Waiting for their review';
  }
  return 'Assigned';
}

/** When a card status change should ping pending approvers. */
export function reviewGateNotifyKind(
  oldStatus: CardStatus,
  newStatus: CardStatus,
): 'entered_review' | 'done_without_review' | null {
  if (newStatus === 'under_review' && oldStatus !== 'under_review') return 'entered_review';
  if (newStatus === 'done' && oldStatus !== 'under_review' && oldStatus !== 'done') {
    return 'done_without_review';
  }
  return null;
}
